import {
	CatalogCategory,
	CatalogTile,
	CatalogTileRecurrence,
	CatalogTileRequirement,
} from "../config/tileCatalog";

export enum CatalogFilter {
	ALL = "ALL",
	RECURRING = "RECURRING",
	ONE_TIME = "ONE_TIME",
	NO_REQUIREMENTS = "NO_REQUIREMENTS",
}

const searchableRequirementLabels: Record<CatalogTileRequirement, string> = {
	[CatalogTileRequirement.LINKEDIN_ACCOUNT]: "linkedin account",
	[CatalogTileRequirement.SALES_NAVIGATOR]: "sales navigator",
	[CatalogTileRequirement.COMPANY_ADMIN]: "company admin",
	[CatalogTileRequirement.WEBSITE_SCRAPE_CREDITS]:
		"scraping credits scrape credits website mobile app scrape",
	[CatalogTileRequirement.TRACKER_AS_LIST_FEATURE]: "tracker",
	[CatalogTileRequirement.SMART_LIST_FEATURE]: "smart list",
};

const hiddenRequirements: CatalogTileRequirement[] = [
	CatalogTileRequirement.TRACKER_AS_LIST_FEATURE,
	CatalogTileRequirement.SMART_LIST_FEATURE,
];

export const displayRequirements = (tile: CatalogTile): CatalogTileRequirement[] =>
	tile.requirements.filter((requirement) => !hiddenRequirements.includes(requirement));

const matchesFilter = (tile: CatalogTile, filter: CatalogFilter): boolean => {
	if (filter === CatalogFilter.RECURRING) {
		return tile.recurrences.includes(CatalogTileRecurrence.RECURRING);
	}

	if (filter === CatalogFilter.ONE_TIME) {
		return tile.recurrences.includes(CatalogTileRecurrence.ONE_TIME);
	}

	if (filter === CatalogFilter.NO_REQUIREMENTS) {
		return displayRequirements(tile).length === 0;
	}

	return true;
};

const buildSearchIndex = (tile: CatalogTile, includeDescription: boolean): string =>
	[
		tile.title,
		...(includeDescription ? [tile.description] : []),
		tile.keywords,
		...tile.requirements.map((requirement) => searchableRequirementLabels[requirement]),
	]
		.join(" ")
		.toLowerCase();

const tokenizeQuery = (query: string): string[] =>
	query.split(/\s+/).filter((token) => token.length >= 2);

const matchesQuery = (tile: CatalogTile, query: string): boolean => {
	const normalizedQuery = query.trim().toLowerCase();

	if (!normalizedQuery) {
		return true;
	}

	// Exact / phrase match across all tile text (including the description).
	if (buildSearchIndex(tile, true).includes(normalizedQuery)) {
		return true;
	}

	// Partial match: the tile matches only when EVERY query token hits its curated index
	// (title + keywords + requirement labels) — AND semantics, so "linkedin search" requires
	// both words, not either.
	const tokens = tokenizeQuery(normalizedQuery);

	if (tokens.length === 0) {
		return false;
	}

	const tokenIndex = buildSearchIndex(tile, false);

	return tokens.every((token) => tokenIndex.includes(token));
};

export const filterCatalogTiles = (
	tiles: CatalogTile[],
	query: string,
	filter: CatalogFilter
): CatalogTile[] =>
	tiles.filter((tile) => matchesFilter(tile, filter) && matchesQuery(tile, query));

export const ALL_CATEGORIES = "ALL";
export const FAVORITES_CATEGORY = "FAVORITES";

export type CatalogCategorySelection =
	| CatalogCategory
	| typeof ALL_CATEGORIES
	| typeof FAVORITES_CATEGORY;

const matchesCategory = (
	tile: CatalogTile,
	selection: CatalogCategorySelection,
	favorites: Set<string>
): boolean => {
	if (selection === ALL_CATEGORIES) {
		return true;
	}

	if (selection === FAVORITES_CATEGORY) {
		return favorites.has(tile.id);
	}

	return tile.categories.includes(selection);
};

export const filterCatalogTilesByCategory = (
	tiles: CatalogTile[],
	query: string,
	selection: CatalogCategorySelection,
	favorites: Set<string>
): CatalogTile[] =>
	tiles.filter((tile) => matchesCategory(tile, selection, favorites) && matchesQuery(tile, query));

export type CatalogCounts = Record<CatalogCategorySelection, number>;

// Counts are derived from the full tile set (ignoring the search query) so the rail totals
// never drift from the catalog, mirroring the mockup's computeCounts.
export const countCatalogTiles = (tiles: CatalogTile[], favorites: Set<string>): CatalogCounts => {
	const counts = {
		[ALL_CATEGORIES]: tiles.length,
		[FAVORITES_CATEGORY]: tiles.filter((tile) => favorites.has(tile.id)).length,
	} as CatalogCounts;

	Object.values(CatalogCategory).forEach((category) => {
		counts[category] = tiles.filter((tile) => tile.categories.includes(category)).length;
	});

	return counts;
};

export const getAiRecommendationCandidates = (
	tiles: CatalogTile[],
	filter: CatalogFilter
): CatalogTile[] => tiles.filter((tile) => matchesFilter(tile, filter));

export const getCatalogRecommendations = (tiles: CatalogTile[], query: string): CatalogTile[] =>
	filterCatalogTiles(tiles, query, CatalogFilter.ALL).slice(0, 3);
